import * as React from 'react';
import {
  StepContentDefinition,
  StepContentProps,
  StepperContentToRender,
} from './step-content';
import { BaSeStepHeader, StepHeaderProps } from './step-header';
import { StepperContainer, StepsContainer } from './stepper-styled';
import { useStepperTransition } from './use-stepper-transition';

export type StepProps = Pick<
  StepHeaderProps,
  | 'title'
  | 'iconName'
  | 'isDisabled'
  | 'isClickable'
  | 'isCompleted'
  | 'tooltip'
  | 'withError'
> &
  Pick<StepContentDefinition, 'content'> &
  Partial<Pick<StepContentProps, 'resumeOnClosed'>>;

export interface StepperProps
  extends Pick<
    StepHeaderProps,
    | 'activeColor'
    | 'completedColor'
    | 'uncompletedColor'
    | 'orientation'
    | 'activeWithErrorColor'
    | 'uncompletedWithErrorColor'
    | 'completedWithErrorColor'
  > {
  steps: StepProps[] | number;
  currentStep: number;
  withTransition?: boolean;
  onStepChange: (step: number) => void;
  preserveContent?: boolean;
}

export const BaSeStepper: React.FC<StepperProps> = ({
  activeColor,
  completedColor,
  uncompletedColor,
  activeWithErrorColor,
  completedWithErrorColor,
  uncompletedWithErrorColor,
  currentStep = 1,
  withTransition = false,
  preserveContent = false,
  orientation = 'horizontal',
  steps: initialStepper,
  onStepChange,
}: StepperProps) => {
  const transition = useStepperTransition(currentStep, withTransition);

  const steps = React.useMemo(() => {
    if (typeof initialStepper === 'number') {
      return Array<StepProps>(initialStepper).fill({});
    }
    return initialStepper;
  }, [initialStepper]);

  if (!Array.isArray(steps) || steps?.length === 0) {
    throw Error('Prop "steps" é obrigatória');
  }

  if (!onStepChange) {
    throw Error('Prop "onStepChange" é obrigatória');
  }

  React.useEffect(() => {
    onStepChange(currentStep);
  }, [currentStep, onStepChange]);

  return (
    <StepperContainer orientation={orientation}>
      <nav>
        <StepsContainer orientation={orientation}>
          {steps.map(
            (
              {
                title,
                iconName,
                isDisabled,
                isClickable,
                isCompleted,
                tooltip,
                withError,
              },
              index,
            ) => (
              <React.Fragment key={index}>
                <BaSeStepHeader
                  activeWithErrorColor={activeWithErrorColor}
                  completedWithErrorColor={completedWithErrorColor}
                  uncompletedWithErrorColor={uncompletedWithErrorColor}
                  steps={steps}
                  withError={withError}
                  isDisabled={isDisabled}
                  isClickable={isClickable}
                  tooltip={tooltip}
                  title={title}
                  iconName={iconName}
                  currentStep={index + 1}
                  activeColor={activeColor}
                  completedColor={completedColor}
                  uncompletedColor={uncompletedColor}
                  isActive={currentStep === index + 1}
                  isCompleted={isCompleted ?? currentStep > index + 1}
                  orientation={orientation}
                  onActivated={() => onStepChange(index + 1)}
                  onStepChange={onStepChange}
                  transition={transition}
                />
              </React.Fragment>
            ),
          )}
        </StepsContainer>
      </nav>
      {orientation === 'horizontal' && (
        <StepperContentToRender
          preserveContent={preserveContent}
          orientation={orientation}
          transition={transition}
          steps={steps}
          currentStep={currentStep}
          onStepChange={onStepChange}
        />
      )}
    </StepperContainer>
  );
};

BaSeStepper.displayName = 'BaSeStepper';
